import { delay, fork, SagaGenerator, select } from 'typed-redux-saga';

import { RootState } from '../../../../../../init-store';
import {
    selectIsLowMinFrameDurationEnabled,
    selectIsRvfcSkipEnabled,
    selectIsUncappedRecorderFpsEnabled,
    selectIsWorkerPreScaleEnabled,
} from '../../../../../shared/features/flags/selectors';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import {
    DRAWING_START_MARK,
    DRAWING_TIME_MEASURE,
    FRAME_START_MARK,
    FRAME_TIME_MEASURE,
} from '../../../../../shared/helpers/performance';
import { Participant, ParticipantDrawerParams, StreamToVideosMap } from '../../../types';
import { drawConference } from '../../record/drawer';
import { enableRvfcSkip } from '../../record/drawer/rvfc-skip/optimizer';
import { RecorderVideo } from './RecorderVideo';

export type RecordHelpers = {
    getAudioStreamTracks: (state: RootState) => MediaStreamTrack[];
    getParticipantDrawerParams: (
        participant: Participant,
        streamToVideosMap: StreamToVideosMap,
        isDrawDesktopTrack: boolean,
        isDrawPresenter: boolean
    ) => ParticipantDrawerParams;
    canStartRecording: () => boolean;
};

const logger = createLogger('room-agent-recorder');

interface RecorderParams {
    canvasContext: CanvasRenderingContext2D;
    frameDuration: number;
    minFrameDuration: number;
    width: number;
    height: number;
}

let wasDrawFrameError = false;

const drawFrame = (state: RootState, recordContext: RecorderParams, helpers: RecordHelpers) => {
    const { canvasContext, width, height } = recordContext;

    try {
        drawConference(canvasContext, { width, height }, state, true, helpers);
    } catch (error) {
        // Залогируем ошибку в бустер, но не будем спамить каждый кадр. Только один раз
        if (!wasDrawFrameError) {
            wasDrawFrameError = true;

            logger.error(error, {
                method: 'drawFrame',
            });
        } else {
            logger.debug('recorder', error);
        }
    }
};

function* drawingFramesSaga(params: RecorderParams, helpers: RecordHelpers): SagaGenerator<void> {
    while (true) {
        const startTime = performance.now();
        performance.mark(FRAME_START_MARK);

        const state = yield* select();
        performance.mark(DRAWING_START_MARK);
        drawFrame(state, params, helpers);
        performance.measure(DRAWING_TIME_MEASURE, DRAWING_START_MARK);

        const restTime = params.frameDuration - (performance.now() - startTime);

        // Рассчитываем, через сколько рисовать следующий кадр. Но ждем не меньше какого-то лимита,
        // чтобы не морозить страницу. Пусть лучше fps отрисовки чуть просядет
        const timeout = Math.max(params.minFrameDuration, restTime);

        yield* delay(timeout);
        performance.measure(FRAME_TIME_MEASURE, FRAME_START_MARK);
        performance.clearMarks(DRAWING_START_MARK);
        performance.clearMarks(FRAME_START_MARK);
    }
}

interface StartRecordSagaParams {
    video: RecorderVideo;
    fps: number;
    /**
     * @description хелперы под SDK
     */
    helpers: RecordHelpers;
}

export function* startRecordSaga({ video, fps = 25, helpers }: StartRecordSagaParams) {
    logger.debug('start recording');
    wasDrawFrameError = false;

    const uncappedFps = yield* select(selectIsUncappedRecorderFpsEnabled);
    const isLowMinFrameDurationEnabled = yield* select(selectIsLowMinFrameDurationEnabled);

    // worker-prescale (off-thread ресайз) включается своим флагом и живёт внутри rVFC
    // как primary-путь; синхронный кэш — fallback.
    const isWorkerPreScaleEnabled = yield* select(selectIsWorkerPreScaleEnabled);
    if (yield* select(selectIsRvfcSkipEnabled)) {
        enableRvfcSkip({ isWorkerEnabled: isWorkerPreScaleEnabled });
    }

    // Минимальная задержка между кадрами (мс): 15 — дефолт, 4 — для SIP.
    // 4 — практический минимум setTimeout (кламп вложенных таймеров ~4 мс), меньше не даёт;
    // на тяжёлой сцене это поднимает FPS отрисовки, не подвешивая страницу.
    const baseMinFrameDuration = isLowMinFrameDurationEnabled ? 4 : 15;

    yield* fork(
        drawingFramesSaga,
        {
            frameDuration: uncappedFps ? 0 : 1000 / fps,
            minFrameDuration: uncappedFps ? 0 : baseMinFrameDuration,
            canvasContext: video.ctx,
            ...video.resolution,
        },
        helpers
    );
}
